//@flow
import React, {useRef, useCallback, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Button,
  Snackbar,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import poweredBy from '../../../../assets/img/poweredBy.png';
import shippingIcon from '../../../../assets/img/shippingIcon.png';
import {
  formatAddressString,
  getAddressFromShippingDetail,
} from '../../../map/utils';
import {toPng} from 'html-to-image';
import {getFontEmbedCSS} from '../../../../utils/font';
import {
  logCaptureImages,
  buildImageErrorHandler,
} from '../../../../utils/capture';

import {formatDateToCustomFormat} from '../../utils';
import {INPUT_TYPE} from '../../../../entities';

import type {
  Order,
  Customer,
  OrderAdditionalInformation,
} from '../../../../entities';

type Props = {|
  customer: Customer,
  storeLogo?: string,
  order: Order,
  storeName: string,
|};

const useStyles = makeStyles(theme => ({
  canvas: {
    position: 'absolute',
    opacity: 0,
    zIndex: -1,
    left: 0,
    top: 0,
    background: 'white',
    padding: '16px',
    width: '340px',
  },
  button: {
    marginTop: '12px',
    marginBottom: '12px',
    borderRadius: '8px',
    border: '1px solid #7B39FF',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'unset',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      width: '90%',
    },
  },
  butonContent: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  informationContent: {
    background: '#F9F9FB',
    padding: '16px',
    overflowWrap: 'break-word',
  },
  label: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    color: '#464854',
    overflowWrap: 'break-word',
  },
  description: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '22px',
    color: '#2F3037',
    overflowWrap: 'break-word',
  },
  logo: {
    width: '120px',
    height: 'auto',
  },
  logoWrapper: {
    borderRight: '1px solid #EDEDED',
    paddingRight: '16px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  smallLabel: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '22px',
    color: '#464854',
  },
  smallDescription: {
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '14px',
    lineHeight: '18px',
    color: '#464853',
    overflowWrap: 'break-word',
  },
  poweredBy: {
    width: '100%',
    maxWidth: '120px',
    height: 'auto',
    display: 'flex',
    margin: 'auto',
  },
  smallSection: {
    marginLeft: '16px',
    overflowWrap: 'break-word',
  },
}));

export default function ShippingLabel(props: Props) {
  const {customer, storeLogo, storeName, order} = props;
  const classes = useStyles(props);
  const [alertOpen, setAlertOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const refVoucherTicket = useRef<HTMLDivElement | null>(null);

  const deliveryMethod = order.deliveryMethod;

  const onButtonClick = useCallback(async () => {
    if (refVoucherTicket.current === null || isDownloading) {
      return;
    }
    setIsDownloading(true);
    logCaptureImages(refVoucherTicket.current, 'shipping-label');
    const fontEmbedCSS = await getFontEmbedCSS();
    toPng(refVoucherTicket.current, {
      style: {
        opacity: 1,
        display: 'block',
      },
      cacheBust: true,
      fontEmbedCSS,
      onImageErrorHandler: buildImageErrorHandler('shipping-label'),
    })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = `${order.id}_label.png`;
        link.href = dataUrl;
        console.log({
          dataUrl,
        });
        link.click();
        setAlertOpen(true);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [order.id, isDownloading]);

  const shippingAddress = useMemo(() => {
    if (order) {
      const {shippingDetail} = order;
      const {
        address1,
        address2,
        admin1 = '',
        admin2 = '',
        admin3 = '',
      } = getAddressFromShippingDetail(shippingDetail);

      const admins = formatAddressString(admin1, admin2, admin3);

      const fullAddress = (
        <div>
          {address1}
          {address2 && (
            <>
              <br />
              {address2}
            </>
          )}
          <br />
          {admins}
        </div>
      );

      if (admin1) return fullAddress;
      else
        return (
          <>
            <div>{address1}.</div>
            <div style={{marginBottom: '4px'}}>{address2}</div>
          </>
        );
    } else return '';
  }, [order]);

  const buildAdditionalInformationInfo = (oa: OrderAdditionalInformation) => {
    const {
      additionalInformation,
      storeOrderAdditionalInformation: {inputType},
    } = oa;

    if (inputType === INPUT_TYPE.CHECKLIST) {
      if (additionalInformation.value instanceof Array) {
        return additionalInformation.value.join(', ') ?? '--';
      } else return '--';
    } else if (inputType === INPUT_TYPE.DATE_PICKER) {
      return typeof additionalInformation.value === 'string'
        ? formatDateToCustomFormat(additionalInformation.value)
        : '--';
    }

    return oa.additionalInformation.value[0] ?? '';
  };

  return (
    <div>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert severity="success" onClose={() => setAlertOpen(false)}>
          Imagen descargada
        </Alert>
      </Snackbar>
      <div style={{position: 'relative'}}>
        <div ref={refVoucherTicket} className={classes.canvas}>
          <div className={classes.row}>
            <div className={classes.logoWrapper}>
              <img alt={storeName} src={storeLogo} className={classes.logo} />
            </div>
            <div className={classes.smallSection}>
              <div className={classes.smallLabel}>Número de orden:</div>
              <div className={classes.smallDescription}>{order.id}</div>
            </div>
          </div>
          <br />
          <div className={classes.informationContent}>
            <div className={classes.label}>Nombre del cliente: </div>
            <div
              className={classes.description}
            >{`${customer.firstName} ${customer.lastName}`}</div>
            <br />
            {customer.identificationNumber && (
              <>
                <div className={classes.label}>DNI: </div>
                <div className={classes.description}>
                  {customer.identificationNumber}
                </div>
                <br />
              </>
            )}
            <div className={classes.label}>
              {deliveryMethod === 'PICK_UP'
                ? 'Recojo en tienda:'
                : 'Dirección de entrega:'}
            </div>
            <div className={classes.description}>{shippingAddress}</div>
            <br />
            <div className={classes.label}>Teléfono del cliente: </div>
            <div className={classes.description}>{customer.phone}</div>
            <br />
            {order.additionalInformation &&
              order.additionalInformation.length > 0 && (
                <div>
                  <Typography className={classes.label}>
                    Información Adicional
                  </Typography>
                  {order.additionalInformation.map((oa, index: number) => (
                    <Typography
                      key={`receipt-oa-${index}`}
                      className={classes.description}
                      gutterBottom
                    >
                      <b>{oa.storeOrderAdditionalInformation.name}: </b>
                      <span>{buildAdditionalInformationInfo(oa)}</span>
                    </Typography>
                  ))}
                </div>
              )}

            <br />
            <br />
          </div>
          <br />
          <img
            alt="powered by tiendada"
            className={classes.poweredBy}
            src={poweredBy}
          />
        </div>

        <div style={{textAlign: 'center'}}>
          <Button
            color="primary"
            onClick={onButtonClick}
            className={classes.button}
            fullWidth
            variant="outlined"
            startIcon={
              isDownloading ? (
                <CircularProgress size={20} />
              ) : (
                <img src={shippingIcon} alt="shipping-label" />
              )
            }
          >
            <div className={classes.butonContent}>Descargar Etiqueta</div>
          </Button>
        </div>
      </div>
    </div>
  );
}

ShippingLabel.propTypes = {
  customer: PropTypes.object.isRequired,
  storeLogo: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
};
